/**
 * IndexedDB wrapper for the 32-byte AES key.
 *
 * The key lives device-bound and unsynced — that's the whole point of
 * the silent-auto-unlock model. Telegram CloudStorage holds the encrypted
 * blob; we hold the key here; the user holds the mnemonic backup as the
 * ultimate recovery.
 *
 * If a user clears browser data / TG cache, the key is gone — but the
 * encrypted blob is still in CloudStorage. Restoring requires the user
 * to paste their mnemonic, which we then re-encrypt with a fresh key.
 */

const DB_NAME = "teleswap-wallet";
const DB_VERSION = 1;
const STORE = "keys";
const KEY_ID = "v1";

// Safari private mode + some embedded webviews leave IDB requests in
// "pending" forever instead of erroring. Cap every op so wallet setup
// can't deadlock waiting on it.
const IDB_TIMEOUT_MS = 8_000;

const withTimeout = <T>(p: Promise<T>, label: string): Promise<T> =>
  new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      reject(new Error(`${label} timed out after ${IDB_TIMEOUT_MS / 1000}s`));
    }, IDB_TIMEOUT_MS);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });

const openDb = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    req.onsuccess = () => resolve(req.result);
    req.onblocked = () => reject(new Error("IndexedDB open blocked by another tab"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
  });
};

type KeyRecord = { id: string; aesKey: Uint8Array; createdAt: number };

const tx = <T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | Promise<T>,
): Promise<T> => {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const store = transaction.objectStore(STORE);
        const out = fn(store);
        if (out instanceof Promise) {
          out.then(resolve, reject);
          return;
        }
        out.onsuccess = () => resolve(out.result);
        out.onerror = () => reject(out.error ?? new Error("IndexedDB request failed"));
      }),
  );
};

/** Fetch the stored 32-byte AES key, or null if none. */
export const idbGetKey = async (): Promise<Uint8Array | null> => {
  try {
    const rec = await tx<KeyRecord | undefined>("readonly", (store) =>
      store.get(KEY_ID) as IDBRequest<KeyRecord | undefined>,
    );
    return rec?.aesKey ?? null;
  } catch {
    return null;
  }
};

/** Persist a 32-byte AES key. Overwrites any existing record. */
export const idbSetKey = async (aesKey: Uint8Array): Promise<void> => {
  if (aesKey.length !== 32) throw new Error("AES key must be exactly 32 bytes.");
  await withTimeout(
    tx<IDBValidKey>("readwrite", (store) =>
      store.put({ id: KEY_ID, aesKey, createdAt: Date.now() }),
    ),
    "IndexedDB key write",
  );
};

/** Delete the stored key. Does not throw if missing. */
export const idbClearKey = async (): Promise<void> => {
  try {
    await tx<undefined>("readwrite", (store) => store.delete(KEY_ID));
  } catch {
    // ignore — clearing a non-existent key is fine
  }
};

/** True if IndexedDB is usable at all (some browsers / private modes lock it). */
export const isIndexedDbAvailable = (): boolean => {
  try {
    return typeof indexedDB !== "undefined";
  } catch {
    return false;
  }
};
